import { CreditCard as CreditCardIcon } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Card, Chip, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import GetStartedDefaultPlans from './getStartedDefaultPlans';
import { CommonHelper } from 'src/utils/commonHelper';
import { getStartedCardSx, getStartedHeaderSx, StepHeader } from './getStartedShared';

export default function GetStartedPaymentStep({ paymentComplete, savingStep, isPlanOverMode = false, onSkip }) {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const isPortalAdmin = Boolean(CommonHelper.GetBrandInfoFromLocalStorage('is_portal_admin'));
  const title = isPlanOverMode
    ? translate('get_started_page.payment.choose_plan_title', { defaultValue: 'Choose a plan' })
    : translate('get_started_page.payment.title', { defaultValue: 'Payment' });
  const description = isPlanOverMode
    ? translate('get_started_page.payment.plan_over_description', {
        defaultValue: 'Your trial period is over. Select a lifetime plan to continue using your workspace.',
      })
    : translate('get_started_page.payment.description', {
        defaultValue: 'Choose lifetime access now or continue with your trial plan.',
      });

  return (
    <Card sx={getStartedCardSx(theme)}>
      <Stack spacing={2.5}>
        <Box
          sx={{
            ...getStartedHeaderSx(theme, isPlanOverMode ? 'warning' : 'primary'),
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Box>
              <StepHeader
                icon={<CreditCardIcon />}
                title={title}
                description={description}
                done={paymentComplete && !isPlanOverMode}
              />
              {isPlanOverMode && (
                <Chip
                  color="warning"
                  label={translate('get_started_page.payment.required_chip', { defaultValue: 'Payment required' })}
                  size="small"
                  sx={{ mt: 1.5, fontWeight: 800 }}
                />
              )}
              {isPlanOverMode && (
                <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 760 }}>
                  {isPortalAdmin
                    ? translate('get_started_page.payment.plan_over_admin_message', {
                        defaultValue:
                          'We paused access because the trial plan expired. Complete payment below; after payment succeeds you can enter the app again.',
                      })
                    : translate('get_started_page.payment.plan_over_member_message', {
                        defaultValue:
                          'We paused access because the trial plan expired. Ask your organization admin to complete billing so you can enter the app again.',
                      })}
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
        {isPortalAdmin ? (
          <>
            {!paymentComplete && !isPlanOverMode && (
              <Stack direction="row" justifyContent="flex-end">
                <LoadingButton
                  variant="contained"
                  color="primary"
                  loading={savingStep === 'payment'}
                  onClick={onSkip}
                  sx={{ minWidth: 180 }}
                >
                  {translate('get_started_page.payment.skip_trial_button', { defaultValue: 'Skip and use trial' })}
                </LoadingButton>
              </Stack>
            )}
            <Box id="lifetime-payment-options">
              <GetStartedDefaultPlans />
            </Box>
          </>
        ) : (
          <Alert
            severity="warning"
            sx={{
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
              '& .MuiAlert-message': { width: 1 },
            }}
          >
            <Typography variant="subtitle1" fontWeight={900}>
              {translate('get_started_page.payment.billing_admin_only_title', {
                defaultValue: 'Billing is available only for the organization admin',
              })}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {translate('get_started_page.payment.billing_admin_only_message', {
                defaultValue:
                  'Please ask your organization admin or portal admin to complete the payment. Once billing is completed, refresh this page or log in again.',
              })}
            </Typography>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}
